import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { useLazyQuery } from "@apollo/client";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

// Custom.
import Wrapper from "../Content/Wrapper";
import Report from "../Content/Report";
import Row from "../Content/Row";
import Header from "../Content/Header";
import { GET_STATISTICS_CERTIFICATES } from "api";
import { GeneralObject } from "@vocollege/app/dist/global";
import VoLoader from "@vocollege/components/dist/VoLoader";
import Stack from "@mui/material/Stack";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  getStatisticsDataWithChildren,
  getStatisticsTotal,
} from "services/Utils";
import { DownloadExcelButtonProps } from "@vocollege/components/dist/DownloadExcelButton";
import { getExcelDataWithChildren, sortByLabel } from "../Utils";

const StatisticsCertificates: React.FC = () => {
  const [parsedData, setParsedData] = useState<GeneralObject>([]);
  const [headerParams, setHeaderParams] = useState<GeneralObject>({});
  const [excelData, setExcelData] = useState<DownloadExcelButtonProps | null>(
    null
  );

  // Methods.

  const handleHeaderChange = (params: GeneralObject) => {
    setHeaderParams(params);
    loadQuery({
      variables: {
        from: params.from,
        to: params.to,
        groupBy: params.groupBy,
      },
    });
  };

  // Api.

  const [loadQuery, { data, loading, variables }] = useLazyQuery(
    GET_STATISTICS_CERTIFICATES,
    {
      fetchPolicy: "network-only",
      onCompleted: (loadedData) => {
        setParsedData(
          getStatisticsDataWithChildren(
            loadedData?.certStatisticsCertificates.data,
            headerParams?.groupBy
          )
        );
      },
    }
  );

  // Effects.

  useEffect(() => {
    setExcelData(
      getExcelDataWithChildren(
        data,
        parsedData,
        variables,
        "certStatisticsCertificates",
        I18n.get.certificate.label
      )
    );
  }, [parsedData, data, variables]);

  return (
    <Wrapper title={I18n.get.certificate.labels.certificates} hideTitleOnPrint>
      <Header
        filters={["from", "to", "groupBy"]}
        onChange={handleHeaderChange}
        excel={excelData || undefined}
      />
      {!loading && data && (
        <Box
          sx={{
            mt: 4,
            "@media print": {
              mt: 0,
            },
          }}>
          <Report
            title={I18n.get.certificate.label}
            subtitle={I18n.trans(I18n.get.vocertStatistics.perItem, {
              item: I18n.get.group.categories[variables?.groupBy].toLowerCase(),
            })}
            date={data?.certStatisticsCertificates.createdAt}
            interval={`${variables?.from} - ${variables?.to}`}
            summary={
              <Box>
                <Row
                  label={I18n.get.vocertStatistics.total}
                  value={parsedData.total}
                />
                <Divider variant="middle" sx={{ mt: 5 }} component="div">
                  <Typography component="h4" variant="h6" fontWeight={600}>
                    {I18n.get.vocertStatistics.perEducatorType}
                  </Typography>
                </Divider>
                <Typography
                  component="h6"
                  variant="caption"
                  marginBottom={3}
                  marginTop={1}
                  textAlign="center"
                  lineHeight={1.2}>
                  {I18n.get.vocertStatistics.perEducatorTypeNote}
                </Typography>
                {Object.keys(parsedData.itemsPerEducatorType)
                  .sort((a, b) =>
                    sortByLabel(a, b, parsedData.itemsPerEducatorType)
                  )
                  .map((key: string) => (
                    <Row
                      key={key}
                      label={parsedData.itemsPerEducatorType[key].label}
                      note={parsedData.itemsPerEducatorType[key].note}
                      value={parsedData.itemsPerEducatorType[key].value}
                      size="small"
                    />
                  ))}

                <Divider variant="middle" sx={{ mt: 5 }} component="div">
                  <Typography component="h4" variant="h6" fontWeight={600}>
                    {I18n.trans(I18n.get.vocertStatistics.perItem, {
                      item: I18n.get.group.categories[
                        variables?.groupBy
                      ].toLowerCase(),
                    })}
                  </Typography>
                </Divider>
              </Box>
            }>
            <Stack>
              {Object.keys(parsedData.items).map((itemId: string) => (
                <Box key={itemId}>
                  <Row
                    label={parsedData.items[itemId].title}
                    value={parsedData.items[itemId].total}
                  />

                  {Object.keys(parsedData.items[itemId].children).map(
                    (childId: string) => {
                      const child = parsedData.items[itemId].children[childId];

                      return (
                        <Box
                          key={childId}
                          sx={(theme) => ({
                            borderBottom: `3px solid ${theme.palette.grey[100]}`,
                            ml: headerParams?.groupBy === "region" ? 4 : 0,
                            mt: 2,
                            pb: 1,
                            "&:last-child": {
                              borderBottom: "none",
                            },
                          })}>
                          {headerParams?.groupBy === "region" && (
                            <Row
                              label={child.title}
                              value={child.total}
                              size="small"
                            />
                          )}

                          {headerParams?.groupBy !== "educator" &&
                            Object.keys(child.educators).map(
                              (educatorId: string) => {
                                const educator = child.educators[educatorId];

                                return (
                                  <Box key={educatorId} sx={{ ml: 4 }}>
                                    <Row
                                      label={educator.title}
                                      value={educator.total}
                                      size="small"
                                    />
                                  </Box>
                                );
                              }
                            )}
                        </Box>
                      );
                    }
                  )}
                </Box>
              ))}
            </Stack>
          </Report>
        </Box>
      )}
      {loading && <VoLoader />}
    </Wrapper>
  );
};

export default StatisticsCertificates;
